import { log } from '../log/log';
import type { MessagingClient } from '../messaging/sessionStore';
import { lastMessagePreview } from './lastMessagePreview';

export interface MessageEnvelope {
  contentHash: string;
  conversationId: string;
  epoch: number;
  serverSeq: number;
  senderAccountId: string;
  /** Hex of the sender's MLS UserId (`IncomingMessage.sender_user_id`). With the
   *  shared-identity seed transfer, a sibling device's messages carry OUR own
   *  UserId — the bulletproof self-attribution signal. Optional: absent on
   *  legacy rows / synthetic envelopes. */
  senderUserIdHex?: string | undefined;
  body?: string | undefined;
  sentAt: number;
}

/**
 * Last-message metadata pushed into the conversation row each time
 * the drain processes (or the user sends) a new envelope. Powers the
 * "John: hey, are you free?" preview on every chat-list row.
 */
export interface ConversationLastMessage {
  readonly lastMessageAt: number;
  /**
   * Short text preview. The drain caps to ~120 chars at write time so
   * Dexie rows stay small; the row UI typically renders ~50 chars.
   * Absent for non-text payloads (e.g. attachments-only) — the row
   * falls back to a media-glyph in that case.
   */
  readonly preview?: string;
  /** `accountId` of the message's sender (so the row can show "You:" vs peer name). */
  readonly senderAccountId?: string;
}

export interface PersistenceAdapter {
  writeMessage(envelope: MessageEnvelope): Promise<void>;
  writeBatch(envelopes: MessageEnvelope[]): Promise<void>;
  /**
   * Overload 1 (legacy): timestamp-only. Maintained for callers that
   * haven't been upgraded to the richer signature yet.
   * Overload 2: full last-message metadata. Adapters that implement
   * this also patch `lastMessagePreview` + `lastMessageSenderAccountId`
   * on the conversation row so chat-list rows update in real time.
   */
  updateConversation(
    conversationId: string,
    lastMessage: number | ConversationLastMessage,
  ): Promise<void>;
}

interface SyncState {
  client: MessagingClient | null;
  unsubscribe: (() => void) | null;
  queue: MessageEnvelope[];
  draining: boolean;
}

const MAX_QUEUE_DEPTH = 1000;

const state: SyncState = {
  client: null,
  unsubscribe: null,
  queue: [],
  draining: false,
};

export function isRunning(): boolean {
  return state.client !== null;
}

export async function start(
  client: MessagingClient,
  persistence: PersistenceAdapter,
): Promise<void> {
  if (state.client !== null) {
    log.warn('sync.start already running');
    return;
  }

  state.client = client;
  state.queue = [];
  state.draining = false;

  log.info('sync.start bootstrap draining cursor');

  try {
    try {
      await client.syncConversations();
    } catch (syncErr) {
      const msg = (syncErr as Error).message ?? '';
      // A stale / out-of-order MLS message in the bootstrap drain throws
      // "mls protocol error: Message epoch differs from the group's
      // epoch." (re-applying an already-applied commit, or a duplicate
      // broadcast of our own send). It is BENIGN — the SDK's live
      // `dispatchIncoming` already drops these, and the next sync
      // reconciles. Failing the whole bootstrap on it would skip the
      // `onMessage` registration below and leave the session without a
      // live message stream. Swallow it and continue.
      if (/epoch differs/i.test(msg)) {
        log.warn(`sync.start epoch mismatch on stale message (benign); continuing: ${msg}`);
      } else if (/Welcome must be handled at client level/i.test(msg)) {
        // A Welcome-kind frame reached the SDK's per-conversation
        // `process` path during the catch-up drain. The transport
        // (`PingTransport.fetchSince`) now filters these out so they
        // only flow through the live WS `subscribe` → client-level
        // `joinConversation` path; this branch is a defensive backstop
        // for older SDK/transport builds (and WS-resume races) so a
        // stray Welcome can never abort `syncEngine.start`. Welcomes are
        // joined via the client path, so dropping them here loses no
        // conversations.
        log.warn(`sync.start welcome on in-group path (benign); continuing: ${msg}`);
      } else {
        throw syncErr;
      }
    }

    log.info('sync.start cursor drained; registering onMessage');

    state.unsubscribe = client.onMessage((raw: unknown) => {
      handleEnvelope(raw, persistence);
    });

    log.info('sync.start complete');
  } catch (error) {
    log.error('sync.start failed', error as Error);

    state.client = null;
    state.unsubscribe = null;
    state.queue = [];

    throw error;
  }
}

export async function stop(): Promise<void> {
  if (state.client === null) {
    log.debug('sync.stop already stopped');
    return;
  }

  log.info('sync.stop starting');

  if (state.unsubscribe !== null) {
    state.unsubscribe();
    state.unsubscribe = null;
  }

  await drainQueue();

  state.client = null;
  state.queue = [];

  log.info('sync.stop complete');
}

function handleEnvelope(raw: unknown, persistence: PersistenceAdapter): void {
  const envelope = parseEnvelope(raw);

  if (!envelope) {
    log.warn('sync.handleEnvelope invalid envelope shape');
    return;
  }

  const hash8 = envelope.contentHash.slice(0, 8);

  log.debug('sync.handleEnvelope received', {
    contentHash: hash8,
    conversationId: envelope.conversationId.slice(0, 8),
    epoch: envelope.epoch,
    serverSeq: envelope.serverSeq,
  });

  if (state.queue.length >= MAX_QUEUE_DEPTH) {
    log.error('sync.handleEnvelope queue depth exceeded', undefined, {
      queueDepth: state.queue.length,
      contentHash: hash8,
    });
    return;
  }

  state.queue.push(envelope);

  scheduleDrain(persistence);
}

function parseEnvelope(raw: unknown): MessageEnvelope | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const candidate = raw as Record<string, unknown>;

  // The SDK's `onMessage` callback emits an `IncomingMessage`
  // (ping-openmls-sdk/sdk/js/src/types.ts:32) with snake_case Bytes
  // fields:
  //   { conversation_id, sender_device, epoch, hlc, plaintext, content_hash }
  // The persistence layer's internal `MessageEnvelope` (above) uses
  // camelCase strings + a UI-ready `body`. We adapt at the boundary.
  // We also accept the legacy in-process camelCase shape so anything
  // that calls handleEnvelope synthetically (tests, future macOS
  // bridges) still works.
  if (isSdkIncoming(candidate)) {
    const sdkMsg = candidate as unknown as SdkIncomingShape;
    const conversationId = bytesToHex(sdkMsg.conversation_id);
    const contentHash = bytesToHex(sdkMsg.content_hash);
    // Self-attribution: `sender_user_id` is the sender's shared MLS UserId. With
    // shared-identity linking, a sibling device's messages carry OUR UserId, so
    // `isSelfMlsUserIdHex(senderUserIdHex)` (messagesAdapter) renders them as self
    // with no `/v1/devices` dependency — the bulletproof primary signal.
    // `senderAccountId` stays the device hex so the device-set fallback
    // (`isSelfDeviceIdHex`) and the `memberDeviceAccounts` resolution keep working
    // for devices linked before shared identity.
    const senderUserIdHex =
      sdkMsg.sender_user_id instanceof Uint8Array
        ? bytesToHex(sdkMsg.sender_user_id)
        : undefined;
    const senderAccountId =
      sdkMsg.sender_device instanceof Uint8Array
        ? bytesToHex(sdkMsg.sender_device)
        : '';
    const epoch = typeof sdkMsg.epoch === 'bigint' ? Number(sdkMsg.epoch) : sdkMsg.epoch;
    const sentAt = extractWallMs(sdkMsg.hlc) ?? Date.now();
    const body = decodePlaintextBody(sdkMsg.plaintext);

    const envelope: MessageEnvelope = {
      contentHash,
      conversationId,
      epoch,
      // The SDK doesn't surface the BE's server_seq; use the wall
      // clock so messages from the same conversation sort
      // monotonically by arrival without colliding with the
      // sender's local-echo placeholder.
      serverSeq: sentAt,
      senderAccountId,
      ...(senderUserIdHex !== undefined ? { senderUserIdHex } : {}),
      sentAt,
    };
    if (body !== undefined) {
      envelope.body = body;
    }
    return envelope;
  }

  if (
    typeof candidate.contentHash !== 'string' ||
    typeof candidate.conversationId !== 'string' ||
    typeof candidate.epoch !== 'number' ||
    typeof candidate.serverSeq !== 'number' ||
    typeof candidate.senderAccountId !== 'string' ||
    typeof candidate.sentAt !== 'number'
  ) {
    return null;
  }

  const envelope: MessageEnvelope = {
    contentHash: candidate.contentHash,
    conversationId: candidate.conversationId,
    epoch: candidate.epoch,
    serverSeq: candidate.serverSeq,
    senderAccountId: candidate.senderAccountId,
    sentAt: candidate.sentAt,
  };

  if (typeof candidate.body === 'string') {
    envelope.body = candidate.body;
  }

  return envelope;
}

interface SdkIncomingShape {
  conversation_id: Uint8Array;
  content_hash: Uint8Array;
  plaintext: Uint8Array;
  sender_device: Uint8Array | unknown;
  /** Account-level MLS identity of the sender (shared across the sender's linked
   *  devices). Present from ping-openmls-sdk ≥ the shared-identity build; absent
   *  (`unknown`) from older SDKs, in which case self-attribution falls back to
   *  the device→account map. */
  sender_user_id: Uint8Array | unknown;
  epoch: number | bigint;
  hlc: unknown;
}

function isSdkIncoming(raw: Record<string, unknown>): boolean {
  return (
    raw.conversation_id instanceof Uint8Array &&
    raw.content_hash instanceof Uint8Array &&
    raw.plaintext instanceof Uint8Array
  );
}

function extractWallMs(hlc: unknown): number | null {
  if (!hlc || typeof hlc !== 'object') return null;
  const h = hlc as Record<string, unknown>;
  const w = h.wall_ms;
  if (typeof w === 'number') return w;
  if (typeof w === 'bigint') return Number(w);
  return null;
}

function decodePlaintextBody(bytes: Uint8Array): string | undefined {
  try {
    const text = new TextDecoder().decode(bytes);
    // The chat composer wraps every send in
    //   { kind: "com.ping.text", payload: { body } }
    // (see apps/web/src/features/chat/WebChatRoom.tsx). We unwrap
    // here so the UI's `messages.body` is the human-readable text,
    // not a JSON blob. Non-text kinds (future: attachments, polls)
    // can extend this branch.
    const parsed = JSON.parse(text) as {
      kind?: string;
      payload?: { body?: unknown };
    };
    if (parsed.kind === 'com.ping.text' && typeof parsed.payload?.body === 'string') {
      return parsed.payload.body;
    }
    // Fall back to the raw decoded text — better than dropping the
    // message; the bubble will look weird but the user sees *something*.
    return text;
  } catch {
    return undefined;
  }
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}

function scheduleDrain(persistence: PersistenceAdapter): void {
  if (state.draining) {
    return;
  }

  queueMicrotask(() => {
    drainQueue(persistence).catch((error) => {
      log.error('sync.drainQueue uncaught error', error as Error);
    });
  });
}

async function drainQueue(persistence?: PersistenceAdapter): Promise<void> {
  if (state.draining) {
    return;
  }

  if (state.queue.length === 0) {
    return;
  }

  if (!persistence) {
    return;
  }

  state.draining = true;

  try {
    const batch = state.queue.splice(0, state.queue.length);

    log.debug('sync.drainQueue processing batch', { count: batch.length });

    await persistence.writeBatch(batch);

    // Collapse the batch down to the FRESHEST envelope per conv so
    // the chat-list row's "last message" preview shows the right
    // text even when multiple envelopes arrived in the same drain
    // tick (e.g. WS resume replay).
    const latestByConv = new Map<string, MessageEnvelope>();
    for (const envelope of batch) {
      const existing = latestByConv.get(envelope.conversationId);
      if (existing === undefined || envelope.sentAt > existing.sentAt) {
        latestByConv.set(envelope.conversationId, envelope);
      }
    }

    for (const [conversationId, envelope] of latestByConv) {
      const preview = lastMessagePreview(envelope.body);
      const update: ConversationLastMessage = {
        lastMessageAt: envelope.sentAt,
        ...(preview !== undefined ? { preview } : {}),
        senderAccountId: envelope.senderAccountId,
      };
      await persistence.updateConversation(conversationId, update);
    }

    log.debug('sync.drainQueue complete', { count: batch.length });
  } catch (error) {
    log.error('sync.drainQueue persistence error', error as Error);
  } finally {
    state.draining = false;
  }
}
